import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Property, ApiResponse, LateFeePolicy, PaymentMethodConfig, BookingSettings } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { accountSettingsKeys } from './useAccountSettings'

// Query keys
export const propertyKeys = {
  all: ['properties'] as const,
  lists: () => [...propertyKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...propertyKeys.lists(), filters] as const,
  details: () => [...propertyKeys.all, 'detail'] as const,
  detail: (id: number) => [...propertyKeys.details(), id] as const,
}

// Fetch all properties
export function useProperties(
  page = 1,
  // EP-97: server-side sort (allow-listed keys: updated_at/created_at/name/unit_count).
  sortParams: { sort?: string; sort_order?: string } = {},
) {
  return useQuery({
    queryKey: propertyKeys.list({ page, ...sortParams }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (sortParams.sort) params.sort = sortParams.sort
      if (sortParams.sort_order) params.sort_order = sortParams.sort_order
      const { data } = await api.get<ApiResponse<Property[]>>('/api/properties', {
        params,
      })
      return data
    },
  })
}

// Fetch all properties in one request (capped at 50 per the Q3 performance note).
// Use this when you need the full list for fan-out operations (e.g. BulkReadingsPage
// All Properties mode, or the Sidebar's has-condominium-property CAF nav gate —
// EP-111 T111-8). Keyed separately so it doesn't pollute the paginated cache.
// `enabled` defaults true so existing callers are unaffected; pass false to skip
// the request entirely (e.g. gate it behind a cheaper flag check first).
export function useAllProperties(enabled = true) {
  return useQuery({
    queryKey: [...propertyKeys.all, 'all-flat'] as const,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Property[]>>('/api/properties', {
        params: { page: 1, per_page: 50 },
      })
      return data.data ?? []
    },
    enabled,
  })
}

// Fetch single property
export function useProperty(id: number) {
  return useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Property>>(`/api/properties/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create property
interface CreatePropertyInput {
  name: string
  address?: string
  // EP-111 T111-7 — only meaningful when the account has condominium_enabled;
  // the property form still sends the default 'apartment' value otherwise.
  property_type?: 'apartment' | 'condominium'
  billing_day?: number
  // EP-149 T149-017 (MON-8/T149-014): nullable "due on day N of the billing
  // period". `null` explicitly clears a previously-set day back to "issue
  // date + 15 days"; the field is always sent (never omitted) so a PATCH can
  // actually null it out, not just leave an unrelated field untouched.
  payment_due_day?: number | null
  deposit_type?: 'months' | 'fixed'
  deposit_value?: number
  pricing_formula?: Property['pricing_formula']
  late_fee_policy?: LateFeePolicy
  payment_methods?: PaymentMethodConfig | null
  // EP-136 (T136-026) — the property's own booking-settings overrides. A key
  // absent from this hash inherits the account-level default (see
  // BookingSettings). booking_settings is a jsonb column that replaces
  // wholesale on save, so callers must send the complete set of active
  // overrides, not a partial patch.
  booking_settings?: BookingSettings
  tax_id?: string
  branch_number?: string
  phone?: string
  email?: string
  location_url?: string
}

export function useCreateProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreatePropertyInput) => {
      const { data } = await api.post<ApiResponse<Property>>('/api/properties', {
        property: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, propertyKeys.lists())
    },
  })
}

// Update property
interface UpdatePropertyInput extends Partial<CreatePropertyInput> {
  id: number
}

export function useUpdateProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdatePropertyInput) => {
      const { data } = await api.patch<ApiResponse<Property>>(`/api/properties/${id}`, {
        property: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, propertyKeys.lists())
      invalidateDetailQueries(queryClient, propertyKeys.detail(data.id))
    },
  })
}

// Delete property
// 422 responses carry response.data.error.message — consumers should toast it via useNotificationStore
export function useDeleteProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/properties/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, propertyKeys.lists())
      // Refresh per-entity counts so empty-state seed/affordance gating is accurate.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}
